<div class="card">
  @if (loading()) {
    <app-loading />
  } @else if (providerColumns().length > 0) {
    <div class="provider-results-table-scroll">
      <p-table
        [value]="tableData()"
        class="provider-columns"
        [class.score-bar-layout]="scoreBars()"
        data-testid="provider-results-table"
        showGridlines
      >
        <ng-template #header>
          <tr>
            <th
              class="text-left sticky left-0 z-10 whitespace-nowrap w-0 criteria-column-cell"
              scope="col"
            >
              {{ 'project.steps.providerResults.criteria' | transloco }}
            </th>
            @for (provider of providerColumns(); track provider.hrToolDirectusId) {
              <th class="text-center provider-score-cell" scope="col">
                <span class="provider-column-title">{{ provider.translatedName }}</span>
              </th>
            }
            <th
              class="text-center w-12 action-column-cell"
              scope="col"
              style="border-left: none !important"
            ></th>
          </tr>
        </ng-template>

        <ng-template #body let-criteriaRow>
          <!-- Main Criteria Row -->
          <tr class="criteria-row">
            <td class="sticky left-0 z-10 whitespace-nowrap">
              <div class="flex flex-col">
                <span class="font-semibold">{{ criteriaRow.translatedLabel }}</span>
                @if (criteriaRow.weight !== undefined) {
                  <span class="text-sm opacity-75"
                    >{{ 'project.steps.providerResults.weight' | transloco }}:
                    {{ criteriaRow.weight * 100 | appNumber: '1.0-2' }}%</span
                  >
                }
                <span class="text-sm opacity-75"
                  >{{ maxValueLabelKey | transloco }}: {{ criteriaRow.maxValue }}</span
                >
              </div>
            </td>

            @for (provider of providerColumns(); track provider.hrToolDirectusId) {
              <td class="text-center border-l border-gray-600 provider-score-cell">
                @let data = criteriaRow.providerResultsByHrToolId.get(provider.hrToolDirectusId);
                @if (!data) {
                  <span class="text-gray-400">-</span>
                } @else {
                  @if (isHeatmapMode()) {
                    <div class="heatmap-cell-content">
                      <div
                        class="heatmap-badge"
                        [style.backgroundColor]="getChartColor(data.percentage)"
                        [attr.aria-label]="data.percentage | appNumber: '1.0-0'"
                      >
                        {{ data.percentage | appNumber: '1.0-0' }}%
                      </div>
                    </div>
                  } @else {
                    <app-provider-score-cell
                      [value]="data.value"
                      [percentage]="data.percentage"
                      [display]="scoreDisplay()"
                      [chartData]="data.chartData"
                      [chartOptions]="chartOptions"
                    />
                  }
                }
              </td>
            }
            <td
              class="text-right w-12 pr-2 action-column-cell"
              style="border-left: none !important"
            >
              <button
                type="button"
                class="p-2 hover:bg-gray-700 rounded-sm cursor-pointer"
                [pTooltip]="
                  (criteriaRow.expanded
                    ? 'project.provider.results.hideSubcriteria'
                    : 'project.provider.results.showSubcriteria'
                  ) | transloco
                "
                (click)="toggleRowExpansion(criteriaRow)"
              >
                <i
                  class="pi transition-transform"
                  [class.pi-chevron-up]="criteriaRow.expanded"
                  [class.pi-chevron-down]="!criteriaRow.expanded"
                ></i>
              </button>
            </td>
          </tr>

          <!-- Subcriteria Rows (shown when expanded) -->
          @if (criteriaRow.expanded) {
            @for (subRow of criteriaRow.subCriteria; track subRow.scNumber) {
              <tr class="criteria-row subcriteria-row">
                <td
                  class="sticky left-0 z-10 whitespace-nowrap"
                  style="
                    background-color: color-mix(
                      in srgb,
                      var(--p-surface-900) 50%,
                      var(--p-surface-800) 50%
                    );
                  "
                >
                  <div class="flex flex-col gap-1">
                    <div class="subcriteria-header-row">
                      <span class="subcriteria-label">{{ subRow.translatedLabel }}</span>
                      @if (!isRfqPhase() && allowSubcriteriaAttributeModal()) {
                        <p-button
                          styleClass="provider-results-action-button subcriteria-compare-button"
                          icon="pi pi-sort-alt"
                          [label]="'common.compare' | transloco"
                          severity="secondary"
                          size="small"
                          (onClick)="openSubcriteriaAttributeScores(subRow)"
                        />
                      }
                    </div>
                    @if (subRow.weight !== undefined) {
                      <span class="text-sm opacity-75"
                        >{{ 'project.steps.providerResults.weight' | transloco }}:
                        {{ subRow.weight * 100 | appNumber: '1.0-2' }}%</span
                      >
                    }
                    <span class="text-sm opacity-75"
                      >{{ maxValueLabelKey | transloco }}: {{ subRow.maxValue }}</span
                    >
                  </div>
                </td>

                @for (provider of providerColumns(); track provider.hrToolDirectusId) {
                  <td
                    class="text-center border-l border-gray-600 provider-score-cell"
                    style="
                      background-color: color-mix(
                        in srgb,
                        var(--p-surface-900) 50%,
                        var(--p-surface-800) 50%
                      );
                    "
                  >
                    @let data = subRow.providerResultsByHrToolId.get(provider.hrToolDirectusId);
                    @if (!data) {
                      <span class="text-gray-400">-</span>
                    } @else {
                      @if (isHeatmapMode()) {
                        <div class="heatmap-cell-content">
                          <div
                            class="heatmap-badge"
                            [style.backgroundColor]="getChartColor(data.percentage)"
                            [attr.aria-label]="data.percentage | appNumber: '1.0-0'"
                          >
                            {{ data.percentage | appNumber: '1.0-0' }}%
                          </div>
                        </div>
                      } @else {
                        <app-provider-score-cell
                          [value]="data.value"
                          [percentage]="data.percentage"
                          [display]="scoreDisplay()"
                          [chartData]="data.chartData"
                          [chartOptions]="chartOptions"
                        />
                      }
                    }
                  </td>
                }
                <td
                  class="text-right w-12 pr-2 action-column-cell"
                  style="
                    background-color: color-mix(
                      in srgb,
                      var(--p-surface-900) 50%,
                      var(--p-surface-800) 50%
                    );
                    border-left: none !important;
                  "
                ></td>
              </tr>
            }
          }
        </ng-template>

        <ng-template #emptymessage>
          <tr>
            <td [attr.colspan]="providerColumns().length + 2"><app-no-data /></td>
          </tr>
        </ng-template>

        @if (totalRow(); as total) {
          <ng-template #footer>
            <!-- Spacer Row to visually separate body from footer -->
            <tr class="h-4 transparent-spacer-row">
              <td [attr.colspan]="providerColumns().length + 2"></td>
            </tr>

            <!-- Total Row -->
            <tr class="font-bold border-t border-gray-700">
              <td
                class="sticky left-0 z-10 whitespace-nowrap bg-second-level font-bold border-l border-gray-700 rounded-tl-lg"
              >
                <div class="flex flex-col">
                  <span>{{ total.translatedLabel }}</span>
                  @if (total.weight !== undefined) {
                    <span class="text-sm opacity-75"
                      >{{ 'project.steps.providerResults.weight' | transloco }}:
                      {{ total.weight * 100 | appNumber: '1.0-2' }}%</span
                    >
                  }
                  <span class="text-sm"
                    >{{ maxValueLabelKey | transloco }}: {{ total.maxValue }}</span
                  >
                </div>
              </td>

              @for (provider of providerColumns(); track provider.hrToolDirectusId) {
                <td
                  class="text-center bg-second-level border-l border-t border-gray-600 border-gray-700 provider-score-cell"
                >
                  @let data = total.providerResultsByHrToolId.get(provider.hrToolDirectusId);
                  @if (data) {
                    @if (isHeatmapMode()) {
                      <div class="heatmap-cell-content">
                        <div
                          class="heatmap-badge"
                          [style.backgroundColor]="getChartColor(data.percentage)"
                          [attr.aria-label]="data.percentage | appNumber: '1.0-0'"
                        >
                          {{ data.percentage | appNumber: '1.0-0' }}%
                        </div>
                      </div>
                    } @else {
                      <app-provider-score-cell
                        [value]="data.value"
                        [percentage]="data.percentage"
                        [display]="scoreDisplay()"
                        [chartData]="data.chartData"
                        [chartOptions]="chartOptions"
                        [wideChart]="true"
                      />
                    }
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
              }
              <td
                class="text-right bg-second-level border-t border-gray-600 border-gray-700 rounded-tr-lg border-r border-gray-700 w-12 pr-2 action-column-cell"
                style="border-left: none !important"
              ></td>
            </tr>

            @if (!isRfdPhase() && !isRfqPhase() && allowViewResultsModal()) {
              <!-- Results overview row -->
              <tr>
                <td
                  class="sticky left-0 z-10 whitespace-nowrap bg-first-level border-l border-b border-gray-700 rounded-bl-lg"
                >
                  {{ 'project.steps.rfi.invitationList.resultReview' | transloco }}
                </td>

                @for (provider of providerColumns(); track provider.hrToolDirectusId) {
                  <td
                    class="text-center border-t border-l border-b border-gray-600 border-gray-700 bg-first-level provider-score-cell"
                  >
                    @if (allowIndividualResultsModal()) {
                      <div class="flex justify-center gap-2">
                        <p-button
                          styleClass="provider-results-action-button"
                          icon="pi pi-list"
                          [ariaLabel]="'project.steps.providerResults.showAnswers' | transloco"
                          [pTooltip]="'project.steps.providerResults.showAnswers' | transloco"
                          tooltipPosition="top"
                          severity="secondary"
                          size="small"
                          (onClick)="onViewResultsClick(provider)"
                          data-testid="view-results-btn"
                        />
                        <p-button
                          styleClass="provider-results-action-button"
                          icon="pi pi-chart-bar"
                          [ariaLabel]="
                            'project.steps.providerResults.individualResults' | transloco
                          "
                          [pTooltip]="'project.steps.providerResults.individualResults' | transloco"
                          tooltipPosition="top"
                          severity="secondary"
                          size="small"
                          (onClick)="onIndividualResultsClick(provider)"
                          data-testid="individual-results-btn"
                        />
                      </div>
                    } @else {
                      <div class="flex justify-center">
                        <p-button
                          styleClass="provider-results-action-button"
                          icon="pi pi-chart-bar"
                          [label]="'common.showResults' | transloco"
                          severity="secondary"
                          size="small"
                          (onClick)="onViewResultsClick(provider)"
                          data-testid="view-results-btn"
                        />
                      </div>
                    }
                  </td>
                }
                <td
                  class="text-right border-t border-b border-r border-gray-600 border-gray-700 bg-first-level rounded-br-lg w-12 pr-2 action-column-cell"
                  style="border-left: none !important"
                ></td>
              </tr>
            }
          </ng-template>
        }
      </p-table>
    </div>

    @if (showAiSummary()) {
      <app-provider-results-ai-summary [projectId]="projectId()" [request]="aiSummaryRequest()" />
    }

    @if (showLegend()) {
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <!--  Legend-->
        <div
          class="flex flex-col border-gray-700 rounded-lg border-solid border p-4 wrap-break-word"
        >
          <h3>{{ 'project.steps.providerResults.legend.title' | transloco }}</h3>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3" [style.backgroundColor]="scoreColors.darkGreen"></div>
            <span>90% - 100%</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3" [style.backgroundColor]="scoreColors.lightGreen"></div>
            <span>75% - 90%</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3" [style.backgroundColor]="scoreColors.yellow"></div>
            <span>60% - 75%</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3" [style.backgroundColor]="scoreColors.orange"></div>
            <span>40% - 60%</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3" [style.backgroundColor]="scoreColors.red"></div>
            <span>0% - 40%</span>
          </div>
          @if (!isHeatmapMode()) {
            <div class="flex gap-2 mt-4">
              <span class="font-bold text-sm"
                >{{ 'project.steps.providerResults.legend.valueFormat' | transloco }}:
              </span>
              <span class="text-sm">{{
                'project.steps.providerResults.legend.valueFormatDescription' | transloco
              }}</span>
            </div>
          }
          @if (!isHeatmapMode()) {
            <div class="flex gap-2">
              <span class="font-bold text-sm"
                >{{ 'project.steps.providerResults.legend.weights' | transloco }}:
              </span>
              <span class="text-sm">{{
                'project.steps.providerResults.legend.weightsDescription' | transloco
              }}</span>
            </div>
          }
        </div>

        <div class="flex border-gray-700 rounded-lg border-solid border p-4">
          <div class="flex gap-3 items-start">
            <div
              class="h-10 w-10 min-w-10 rounded-full flex items-center justify-center bg-second-level text-gray-300"
            >
              <i class="pi pi-lightbulb text-base"></i>
            </div>
            <div class="flex flex-col gap-1">
              <h3 class="m-0">{{ 'provider.results.hint.title' | transloco }}</h3>
              <p class="m-0 text-sm opacity-80">
                {{ 'provider.results.hint.description' | transloco }}
              </p>
            </div>
          </div>
        </div>
      </div>
    }
  } @else {
    @if (noDataInfo(); as info) {
      <app-no-data [icon]="info.icon" [label]="info.titleKey" [description]="info.messageKey" />
    }
  }

  @if (!isRfdPhase() && !isRfqPhase() && allowViewResultsModal()) {
    <app-subcriteria-scores-modal
      [visible]="subcriteriaScoresModalVisible()"
      [projectId]="projectId()"
      [toolId]="selectedParticipatingTool()?.id"
      [toolName]="selectedParticipatingTool()?.translatedName"
      [phaseType]="phase()"
      (visibleChange)="subcriteriaScoresModalVisible.set($event)"
    />
  }

  @if (allowIndividualResultsModal()) {
    @if (isRfiPhase()) {
      <app-rfi-individual-results-modal
        [(visible)]="individualResultsModalVisible"
        [toolName]="individualResultsProvider()?.translatedName"
        [hrToolDirectusId]="individualResultsProvider()?.hrToolDirectusId"
        [calculationResult]="calculationResult()"
        [subCriteriaScores]="individualSubCriteriaScores()"
      />
    } @else {
      <app-rfp-individual-results-modal
        [(visible)]="individualResultsModalVisible"
        [toolName]="individualResultsProvider()?.translatedName"
        [results]="rfpIndividualResults()"
      />
    }
  }

  @if (isRfdPhase() && allowSubcriteriaAttributeModal()) {
    <app-rfd-agenda-item-scores-modal
      [visible]="subcriteriaAttributeScoresModalVisible()"
      [subCriteria]="selectedSubCriteria()"
      [providerColumns]="providerColumns()"
      [isExponentialScale]="isExponentialScale()"
      [rfdEvaluationData]="rfdEvaluationData()"
      [rfdScoresByToolId]="rfdScoresByToolId()"
      (visibleChange)="subcriteriaAttributeScoresModalVisible.set($event)"
    />
  } @else if (!isRfqPhase() && allowSubcriteriaAttributeModal()) {
    <app-subcriteria-attribute-scores-modal
      [visible]="subcriteriaAttributeScoresModalVisible()"
      [subCriteria]="selectedSubCriteria()"
      [providerColumns]="providerColumns()"
      [calculationResult]="calculationResult()"
      [isExponentialScale]="isExponentialScale()"
      (visibleChange)="subcriteriaAttributeScoresModalVisible.set($event)"
    />
  }

  @if (!isRfdPhase() && showCompletePhase()) {
    <div class="flex justify-end mt-4">
      <p-button
        [label]="'project.finish.phase.label' | transloco"
        (onClick)="completePhase()"
        data-testid="complete-button"
      ></p-button>
    </div>
  }
</div>
